import { supabase } from '../lib/supabase';

/**
 * Insert an in-app notification into the notifications table.
 * @param {object} params
 * @param {string} params.org_id
 * @param {string} params.type - e.g. 'new_lead', 'job_completed', 'payment_received', etc.
 * @param {string} params.title
 * @param {string} [params.body]
 * @param {string} [params.link]
 */
export async function createInAppNotification({ org_id, type, title, body = null, link = null }) {
  if (!org_id) return;
  try {
    await supabase?.from('notifications')?.insert({ org_id, type, title, body, link });
  } catch (err) {
    console.error('[Notification] Failed to insert in-app notification:', err);
  }
}

/**
 * Send an email notification via the send-notification-email Edge Function.
 * @param {object} params
 * @param {string} params.type - email template type
 * @param {string|string[]} params.to - recipient email(s)
 * @param {object} params.data - additional template data
 */
export async function sendEmailNotification({ type, to, data = {} }) {
  if (!to) return;
  try {
    const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
    const res = await fetch(`${supabaseUrl}/functions/v1/send-notification-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseKey}`,
      },
      body: JSON.stringify({ type, to, ...data }),
    });
    if (!res?.ok) {
      const text = await res?.text();
      console.error('[Notification] Email send failed:', res?.status, text);
    }
  } catch (err) {
    console.error('[Notification] Failed to send email notification:', err);
  }
}

/**
 * Fetch the admin profile email for an org.
 * @param {string} org_id
 * @returns {Promise<{email: string, full_name: string}|null>}
 */
export async function getAdminProfile(org_id) {
  if (!org_id) return null;
  try {
    const { data } = await supabase
      ?.from('profiles')
      ?.select('email, full_name')
      ?.eq('org_id', org_id)
      ?.eq('role', 'admin')
      ?.maybeSingle();
    return data || null;
  } catch (err) {
    console.error('[Notification] Failed to fetch admin profile:', err);
    return null;
  }
}

/**
 * Combined helper: insert in-app notification AND send email to admin.
 * @param {object} params
 * @param {string} params.org_id
 * @param {string} params.type
 * @param {string} params.title
 * @param {string} [params.body]
 * @param {string} [params.link]
 * @param {string} [params.adminEmail]
 * @param {string} [params.orgName]
 * @param {object} [params.emailData] - extra fields for email template
 */
export async function notify({ org_id, type, title, body = null, link = null, adminEmail = null, orgName = 'there', emailData = {} }) {
  // Always create in-app notification
  await createInAppNotification({ org_id, type, title, body, link });

  // Send email if admin email is provided
  if (adminEmail) {
    await sendEmailNotification({
      type,
      to: adminEmail,
      data: { org_name: orgName, ...emailData },
    });
  }
}
